

















































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { DataModules } from "@/store/modules/data";
import { Inheritance } from "@/data/enums/priceLists";

// Types
import { IState } from "@/store";
import { IPricelistForm, IPricelist } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      priceList(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.priceListScope
        });
      },
      priceListScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/scope`](this.priceListId);
      },
      priceLists(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule,
          scope: this.priceListsScope
        });
      },
      priceListsScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/priceListsScope`](this.brandId);
      }
    })
  }
})
export default class AddEditPricelistModal extends Mixins(
  RequestPromises,
  HasValidation,
  BrandsStore
) {
  public $refs!: {
    name: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Getters
  public priceList!: IPricelist;
  public priceListScope!: string;
  public priceLists!: IPricelist[];
  public priceListsScope!: string;

  // Props
  @Prop({ required: true })
  public priceListId!: IPricelist["id"];

  // Variables
  public form: IPricelistForm = {
    name: "",
    inheritance: Inheritance.NOT_INHERITED,
    parent_pricelist_id: null
  };
  public formClone = {} as IPricelistForm;

  public dataModule: string = DataModules.PRICELISTS;

  public isLoading = false;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public created() {
    if (this.contentAvailable) {
      this.isLoading = true;
      this.loadData();
    }
  }

  public async loadData() {
    try {
      if (this.priceListId) {
        await this.getPriceList();

        this.form = this.$_.merge(
          this.form,
          this.$_.pick(this.priceList, ["name", "inheritance"])
        );

        this.form.parent_pricelist_id = this.$_.get(
          this.priceList,
          "parents[0].id",
          null
        );
      }

      await this.getPriceLists();
      this.formClone = this.$_.clone(this.form);
      this.requestError = false;
    } catch (error) {
      this.$buefy.toast.open({
        message: error.message,
        type: "is-danger"
      });
      this.requestError = true;
    }

    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getPriceList() {
    return this.$store.dispatch(`data/${this.dataModule}/get`, {
      id: this.priceListId,
      scope: this.priceListScope,
      vm: this
    });
  }

  public getPriceLists() {
    return this.$store.dispatch(`data/${this.dataModule}/list`, {
      scope: this.priceListsScope,
      vm: this,
      params: { limit: 0, with: "parents" }
    });
  }

  public get formIsValid() {
    if (this.$_.isEmpty(this.form.name)) {
      return false;
    }
    if (this.priceListId && this.$_.isEqual(this.form, this.formClone)) {
      return false;
    }

    return true;
  }

  public get formIsDisabled() {
    return (
      !this.formIsValid || this.hasErrors || this.isProcessing || this.isLoading
    );
  }

  public get filteredPriceLists() {
    return this.$_.filter(
      this.priceLists,
      priceList => priceList.id !== this.priceListId
    );
  }

  public validateBeforeSubmit() {
    if (
      this.priceListId
        ? !this.$userCan(`create_pricelist`)
        : !this.$userCan(`update_pricelist`)
    ) {
      return;
    }

    this.isProcessing = true;

    this.validate()
      .then(result => {
        if (result) {
          return this.priceListId
            ? this.updatePricelist()
            : this.createPricelist();
        }
        throw this.$t("_sentence.form_is_not_valid");
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t(
            this.priceListId
              ? "_.object_successfully_updated"
              : "_.object_successfully_created",
            { object: this.$t("_.price_list") }
          ) as string
        });
        this.$emit("success");
        this.$emit("close");
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public createPricelist() {
    return this.$store.dispatch(`data/${this.dataModule}/create`, {
      data: this.getNewValues()
    });
  }

  public updatePricelist() {
    return this.$store.dispatch(`data/${this.dataModule}/update`, {
      id: this.priceListId,
      scope: this.priceListScope,
      data: this.getNewValues()
    });
  }

  public get i18n() {
    return {
      modalTitle: this.$t(
        this.priceListId ? "_action.edit_object" : "_action.add_object",
        {
          object: this.$t("_.price_list")
            .toString()
            .toLowerCase()
        }
      ),
      modalAction: this.priceListId
        ? this.$t("_action.save")
        : this.$t("_action.add")
    };
  }

  public isDisabled(priceList) {
    return this.$_.get(priceList, "parents", []).length > 0;
  }

  public getNewValues() {
    const newValues: any = this.$_.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });

    if (this.$_.has(newValues, "parent_pricelist_id")) {
      newValues.parent_pricelists =
        newValues.parent_pricelist_id === null
          ? []
          : [newValues.parent_pricelist_id];
      delete newValues.parent_pricelist_id;
    }

    return newValues;
  }

  @Watch("form.inheritance")
  public onInheritanceChange() {
    if (this.form.inheritance !== Inheritance.NOT_INHERITED) {
      return;
    }

    this.form.parent_pricelist_id = null;
  }

  public get contentAvailable() {
    return this.priceListId ? this.$userCan("get_pricelist") : true;
  }

  public get guardFunctionalities() {
    return this.priceListId ? ["get_pricelist"] : true;
  }

  public get isInherited() {
    return this.form.inheritance === Inheritance.INHERITED;
  }

  public beforeDestroy() {
    if (this.priceListId) {
      this.$store.dispatch("data/bin", {
        storeModule: this.dataModule,
        scope: this.priceListScope,
        vm: this
      });
    }

    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      scope: this.priceListsScope,
      vm: this
    });
  }
}
